import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Wallet,
  CircleDollarSign,
  FileClock,
  AlertTriangle,
  CalendarClock,
  CakeSlice,
  Repeat,
  ChevronRight,
} from "lucide-react";
import api from "./api";

const money = (n) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

const compact = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1_000) return "$" + Math.round(n / 1_000) + "K";
  return money(n);
};

const num = (n) => new Intl.NumberFormat("en-CA").format(Math.round(n || 0));

const PIPELINE_STAGES = [
  "Raw Lead",
  "Active Lead",
  "Discovery Booked",
  "Discovery Completed",
  "Client Closed",
];

const PRODUCT_COLORS = {
  "Perm Life": "#b78a2f",
  "Term Life": "#0071e3",
  "Critical Illness": "#ff453a",
  "Disability": "#af52de",
  "Investment": "#30d158",
  "Other": "#8e8e93",
};

export default function Dashboard() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/crm/advisor-metrics").then((r) => setM(r.data));
  }, []);

  if (!m) {
    return (
      <div style={{ color: "var(--crm-text-muted)" }} data-testid="dashboard-loading">
        Loading advisor console…
      </div>
    );
  }

  const b = m.book;

  return (
    <div data-testid="dashboard" className="space-y-10">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1
            className="text-[34px] tracking-tight leading-none"
            style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.02em" }}
          >
            Book of Business
          </h1>
          <div className="mt-2 text-[14px]" style={{ color: "var(--crm-text-muted)" }}>
            {new Date().toLocaleDateString("en-CA", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>
      </header>

      {/* KPI grid */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi
          label="Annual Recurring Premium"
          value={compact(b.arp_inforce)}
          sub={`${num(b.policies_inforce)} policies in force`}
          icon={Shield}
          testid="kpi-arp"
        />
        <Kpi
          label="AUM"
          value={compact(b.aum)}
          sub="Segregated + mutual"
          icon={Wallet}
          testid="kpi-aum"
        />
        <Kpi
          label="Commissions YTD"
          value={compact(b.commissions_ytd)}
          sub={`Est. ${compact(b.commissions_ytd_est)}`}
          icon={CircleDollarSign}
          testid="kpi-comm"
        />
        <Kpi
          label="Pending Apps"
          value={num(b.pending_apps)}
          sub="Waiting APS / U/W"
          icon={FileClock}
          testid="kpi-pending"
        />
        <Kpi
          label="Lapsed / At-Risk"
          value={num(b.lapsed_count)}
          sub="Requires outreach"
          icon={AlertTriangle}
          alert={b.lapsed_count > 0}
          testid="kpi-lapsed"
        />
      </section>

      {/* Today */}
      <section>
        <SectionHeader title="Today" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ActionTile
            to="/crm/followups?bucket=overdue"
            label="Overdue follow-ups"
            value={m.actions.overdue_followups}
            icon={AlertTriangle}
            alert={m.actions.overdue_followups > 0}
            testid="tile-overdue"
          />
          <ActionTile
            to="/crm/followups?bucket=week"
            label="Due this week"
            value={m.actions.followups_due_week}
            icon={CalendarClock}
            testid="tile-week"
          />
          <ActionTile
            to="/crm/clients"
            label={`Reviews · ${new Date().toLocaleString("en-CA", { month: "long" })}`}
            value={m.actions.reviews_this_month}
            icon={Repeat}
            testid="tile-reviews"
          />
          <ActionTile
            to="/crm/contacts"
            label="Birthdays · 14 days"
            value={m.actions.birthdays_next_14d}
            icon={CakeSlice}
            testid="tile-birthdays"
          />
        </div>
      </section>

      {/* Pipeline + Product mix */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <PipelineCard pipeline={m.pipeline} />
        <ProductMixCard mix={m.product_mix} />
      </section>

      {/* Birthdays + Recent cases */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <BirthdayCard birthdays={m.birthdays} />
        <ProvinceCard byProvince={m.by_province || {}} />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ProvinceCard({ byProvince }) {
  const entries = Object.entries(byProvince).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const total = entries.reduce((a, [, v]) => a + v, 0) || 1;
  return (
    <div
      className="lg:col-span-3 rounded-2xl"
      style={{
        background: "var(--crm-glass)",
        border: "1px solid var(--crm-line)",
        boxShadow: "var(--crm-shadow)",
      }}
      data-testid="province-card"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[16px]" style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}>Book by province</div>
            <div className="text-[12px]" style={{ color: "var(--crm-text-muted)" }}>{total.toLocaleString()} contacts</div>
          </div>
        </div>
        <div className="space-y-2.5">
          {entries.map(([prov, n]) => {
            const pct = (n / total) * 100;
            return (
              <div key={prov} className="grid grid-cols-[60px_1fr_60px] items-center gap-3 text-[13px]">
                <div className="text-[11px] uppercase tracking-[0.08em]" style={{ color: "var(--crm-text-muted)" }}>{prov}</div>
                <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: "var(--crm-line-soft)" }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--crm-accent)", transition: "width .6s cubic-bezier(0.32, 0.72, 0, 1)" }} />
                </div>
                <div className="text-right tabular-nums" style={{ color: "var(--crm-text)" }}>{n.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Card({ children, className = "", style, testid, span }) {
  return (
    <div
      className={`rounded-2xl ${className} ${span || ""}`}
      style={{
        background: "var(--crm-glass)",
        border: "1px solid var(--crm-line)",
        boxShadow: "var(--crm-shadow)",
        ...style,
      }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, alert, testid }) {
  return (
    <Card testid={testid}>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div
            className="text-[11px] uppercase tracking-[0.08em]"
            style={{ color: "var(--crm-text-subtle)" }}
          >
            {label}
          </div>
          <Icon
            size={15}
            strokeWidth={1.75}
            style={{ color: alert ? "#ff453a" : "var(--crm-text-subtle)" }}
          />
        </div>
        <div
          className="mt-3 text-[28px] leading-none tabular-nums"
          style={{
            color: alert ? "#ff453a" : "var(--crm-text)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </div>
        <div className="mt-2 text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
          {sub}
        </div>
      </div>
    </Card>
  );
}

function ActionTile({ to, label, value, icon: Icon, alert, testid }) {
  const hasWork = value > 0;
  return (
    <Link to={to} data-testid={testid} className="block group">
      <Card
        className="transition"
        style={{
          background: alert && hasWork ? "#fff5f5" : "var(--crm-glass)",
          borderColor: alert && hasWork ? "rgba(255,69,58,0.25)" : "var(--crm-line)",
        }}
      >
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div
              className="text-[11px] uppercase tracking-[0.08em]"
              style={{ color: alert && hasWork ? "#ff453a" : "var(--crm-text-subtle)" }}
            >
              {label}
            </div>
            <Icon
              size={15}
              strokeWidth={1.75}
              style={{ color: alert && hasWork ? "#ff453a" : "var(--crm-text-subtle)" }}
            />
          </div>
          <div className="flex items-end justify-between mt-3">
            <div
              className="text-[32px] leading-none tabular-nums"
              style={{
                color: alert && hasWork ? "#ff453a" : "var(--crm-text)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
              }}
            >
              {value}
            </div>
            <ChevronRight
              size={16}
              className="opacity-0 group-hover:opacity-60 transition -mr-1"
              style={{ color: "var(--crm-text-muted)" }}
            />
          </div>
        </div>
      </Card>
    </Link>
  );
}

function PipelineCard({ pipeline }) {
  const total = PIPELINE_STAGES.reduce((a, s) => a + (pipeline[s]?.count || 0), 0);
  const totalValue = PIPELINE_STAGES.reduce((a, s) => a + (pipeline[s]?.value || 0), 0);
  return (
    <Card span="lg:col-span-3" testid="pipeline-card">
      <div className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div
              className="text-[16px]"
              style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}
            >
              Pipeline
            </div>
            <div className="text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
              {num(total)} opportunities · {compact(totalValue)} total
            </div>
          </div>
          <Link
            to="/crm/pipeline"
            className="text-[13px] flex items-center gap-0.5 hover:underline"
            style={{ color: "var(--crm-accent)" }}
          >
            Kanban <ChevronRight size={14} />
          </Link>
        </div>
        <div className="space-y-3">
          {PIPELINE_STAGES.map((s) => {
            const d = pipeline[s] || { count: 0, value: 0 };
            const pct = total ? (d.count / total) * 100 : 0;
            return (
              <div key={s} className="grid grid-cols-[170px_1fr_60px_75px] items-center gap-4">
                <div className="text-[13px]" style={{ color: "var(--crm-text)" }}>
                  {s}
                </div>
                <div
                  className="relative h-1.5 rounded-full overflow-hidden"
                  style={{ background: "var(--crm-line-soft)" }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: s === "Client Closed" ? "var(--crm-accent)" : "#0071e3",
                      transition: "width .6s cubic-bezier(0.32, 0.72, 0, 1)",
                    }}
                  />
                </div>
                <div
                  className="text-right tabular-nums text-[13px]"
                  style={{ color: "var(--crm-text)" }}
                >
                  {num(d.count)}
                </div>
                <div
                  className="text-right tabular-nums text-[12px]"
                  style={{ color: "var(--crm-text-muted)" }}
                >
                  {d.value ? compact(d.value) : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function ProductMixCard({ mix }) {
  const entries = Object.entries(mix).sort((a, b) => b[1].premium - a[1].premium);
  const totalPremium = entries.reduce((a, [, v]) => a + v.premium, 0) || 1;
  return (
    <Card span="lg:col-span-2" testid="product-mix-card">
      <div className="p-6">
        <div
          className="text-[16px] mb-1"
          style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}
        >
          In-force Product Mix
        </div>
        <div className="text-[12px] mb-4" style={{ color: "var(--crm-text-muted)" }}>
          By annual premium
        </div>
        <div
          className="flex w-full h-2 rounded-full overflow-hidden mb-5"
          style={{ background: "var(--crm-line-soft)" }}
        >
          {entries.map(([k, v]) => (
            <div
              key={k}
              style={{
                width: `${(v.premium / totalPremium) * 100}%`,
                background: PRODUCT_COLORS[k] || "#8e8e93",
              }}
              title={`${k}: ${money(v.premium)}`}
            />
          ))}
        </div>
        <div className="space-y-2.5">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 text-[13px]">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: PRODUCT_COLORS[k] || "#8e8e93" }}
              />
              <span className="flex-1" style={{ color: "var(--crm-text)" }}>
                {k}
              </span>
              <span
                className="text-[12px] tabular-nums"
                style={{ color: "var(--crm-text-muted)" }}
              >
                {num(v.count)} pol
              </span>
              <span
                className="tabular-nums w-16 text-right"
                style={{ color: "var(--crm-text)" }}
              >
                {compact(v.premium)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function BirthdayCard({ birthdays }) {
  return (
    <Card span="lg:col-span-2" testid="birthdays-card">
      <div className="p-6">
        <div
          className="text-[16px] mb-1"
          style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}
        >
          Upcoming birthdays
        </div>
        <div className="text-[12px] mb-4" style={{ color: "var(--crm-text-muted)" }}>
          Next 14 days
        </div>
        {birthdays.length === 0 ? (
          <div className="text-[13px]" style={{ color: "var(--crm-text-subtle)" }}>
            No client birthdays coming up.
          </div>
        ) : (
          <div className="space-y-1">
            {birthdays.map((b) => (
              <Link
                key={b.contact_id}
                to={`/crm/contacts/${b.contact_id}`}
                className="flex items-center gap-3 rounded-lg -mx-2 px-2 py-2 transition"
                data-testid={`birthday-${b.contact_id}`}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "var(--crm-accent-soft)", color: "var(--crm-accent)" }}
                >
                  <CakeSlice size={14} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] truncate" style={{ color: "var(--crm-text)" }}>
                    {b.name || b.contact_id}
                  </div>
                  <div className="text-[11.5px]" style={{ color: "var(--crm-text-muted)" }}>
                    Turning {b.turning}
                  </div>
                </div>
                <div
                  className="text-[12px] tabular-nums"
                  style={{ color: "var(--crm-accent)" }}
                >
                  {b.days_until === 0 ? "today" : `in ${b.days_until}d`}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function RecentCasesCard({ cases }) {
  const statusColor = (s) => {
    if (!s) return "var(--crm-text-subtle)";
    if (/Approved|Settled/.test(s)) return "#30d158";
    if (/Waiting/.test(s)) return "#ff9f0a";
    if (/Declined|Lapsed/.test(s)) return "#ff453a";
    return "var(--crm-text-muted)";
  };
  return (
    <Card span="lg:col-span-3" testid="recent-cases-card">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div
              className="text-[16px]"
              style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}
            >
              Recent case activity
            </div>
            <div className="text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
              Last 10 cases
            </div>
          </div>
          <Link
            to="/crm/cases"
            className="text-[13px] flex items-center gap-0.5 hover:underline"
            style={{ color: "var(--crm-accent)" }}
          >
            All cases <ChevronRight size={14} />
          </Link>
        </div>
        <div className="text-[13px]">
          <div
            className="grid grid-cols-[80px_1fr_140px_90px_120px] gap-3 py-2 text-[11px] uppercase tracking-[0.08em]"
            style={{
              color: "var(--crm-text-subtle)",
              borderBottom: "1px solid var(--crm-line-soft)",
            }}
          >
            <div>Date</div>
            <div>Client</div>
            <div>Product</div>
            <div className="text-right">Premium</div>
            <div>Status</div>
          </div>
          {cases.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-[80px_1fr_140px_90px_120px] gap-3 py-2.5 items-center"
              style={{ borderBottom: "1px solid var(--crm-line-soft)" }}
            >
              <div
                className="tabular-nums text-[12px]"
                style={{ color: "var(--crm-text-muted)" }}
              >
                {c.case_date?.slice(0, 10) || "—"}
              </div>
              <div className="truncate" style={{ color: "var(--crm-text)" }}>
                {c.contact_name || "—"}
              </div>
              <div className="truncate text-[12px]" style={{ color: "var(--crm-text-muted)" }}>
                {c.product || "—"}
              </div>
              <div
                className="text-right tabular-nums"
                style={{ color: "var(--crm-text)" }}
              >
                {money(c.annual_premium)}
              </div>
              <div
                className="text-[12px] flex items-center gap-1.5 truncate"
                style={{ color: statusColor(c.status) }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: statusColor(c.status) }}
                />
                {c.status || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function SectionHeader({ title }) {
  return (
    <div
      className="text-[20px] mb-4"
      style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.01em" }}
    >
      {title}
    </div>
  );
}
